import React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Reveal from "./Reveal";
import TiltCard from "./TiltCard";
import { profile, stats, youtube } from "../data/content";

function Counter({ value, suffix }) {
  const isDecimal = !Number.isInteger(value);
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 1600, bounce: 0 });
  const display = useTransform(spring, (v) => (isDecimal ? v.toFixed(1) : Math.floor(v)));
  const [text, setText] = React.useState(isDecimal ? "0.0" : 0);

  React.useEffect(() => {
    const unsub = display.on("change", (v) => setText(v));
    return unsub;
  }, [display]);

  return (
    <motion.span onViewportEnter={() => mv.set(value)} viewport={{ once: true, margin: "-60px" }}>
      {text}
      {suffix}
    </motion.span>
  );
}

export default function About() {
  return (
    <section id="about" className="section">
      <div className="container">
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 64, alignItems: "start" }} className="about-grid">
          <div>
            <Reveal>
              <div className="eyebrow">About</div>
              <h2 className="section-title">
                From writing code at a desk job to <span className="gradient-text">building my own.</span>
              </h2>
            </Reveal>

            {profile.bio.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <p style={{ color: "var(--text-muted)", fontSize: 17, lineHeight: 1.8, marginBottom: 18 }}>{p}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <TiltCard>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--accent)", marginBottom: 18 }}>
                whoami --verbose
              </div>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
                {[
                  ["Name", profile.name],
                  ["Base", profile.location],
                  ["Focus", "React Native · Full-Stack"],
                  ["Runs", "Engineer Codewala"],
                  ["Teaches", `${youtube.subs} subscribers`],
                  ["Experience", "6+ years"],
                ].map(([k, v]) => (
                  <li
                    key={k}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom: "1px dashed var(--border)",
                      paddingBottom: 10,
                      fontSize: 15,
                    }}
                  >
                    <span style={{ color: "var(--text-dim)" }}>{k}</span>
                    <span style={{ fontWeight: 500 }}>{v}</span>
                  </li>
                ))}
              </ul>
            </TiltCard>
          </Reveal>
        </div>

        <div
          className="stats-grid"
          style={{
            marginTop: 90,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 24,
          }}
        >
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <div className="card" style={{ textAlign: "center", padding: "28px 20px" }}>
                <div
                  className="gradient-text"
                  style={{ fontFamily: "var(--font-display)", fontSize: 36, fontWeight: 700, marginBottom: 6 }}
                >
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: 14, color: "var(--text-muted)" }}>{s.label}</div>
                {s.note && <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4 }}>{s.note}</div>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .about-grid { grid-template-columns: 1fr !important; }
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 520px) {
          .stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
